<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { BG, classArt } from '../game/art';
import { CLASS_LABELS } from '../game/hero-api';
import FightScreen from './FightScreen.vue';
import { fetchArenaStats, searchArena, acceptArena, cancelArena, type ArenaStats, type ArenaOpponent } from '../game/arena-api';
import { fetchCurrentFight, type FightView } from '../game/mobfight-api';
import { ApiError } from '../../core/network/api';

// Bubble nerve/cash changes up so HeroScreen refreshes the header.
const props = defineProps<{ heroClass?: string }>();
const emit = defineEmits<{ (e: 'updated'): void }>();

const loading = ref(true);
const error = ref('');
const stats = ref<ArenaStats | null>(null);
/** True when `stats` is the last-known standing after a failed reload. */
const stale = ref(false);
const fightInitial = ref<FightView | null>(null); // a bout we started or resumed

/** Small state machine driving the lobby: idle (standing panel + Enter) ->
 *  searching (matchmaking sequence) -> found (concrete opponent, FIGHT or
 *  CANCEL) -> fighting (hand off to FightScreen). */
type PitState = 'idle' | 'searching' | 'found' | 'fighting';
const state = ref<PitState>('idle');
const opponent = ref<ArenaOpponent | null>(null);
const flowError = ref('');
const busy = ref(false);

/** /arena/search resolves almost instantly (bot-only matchmaking — there is
 *  no real opponent to wait on), but a 3-second reveal read as fake theatre
 *  rather than a real queue (QA: "Create queue time - 15-60 sec"). So the
 *  "searching" state is held for a RANDOM 15-60s every time — rolled fresh
 *  per queue entry, not a fixed constant, so it doesn't feel scripted. The
 *  elapsed-seconds counter below is honest: it just counts real time, same
 *  as before, it's only the hold target that grew. */
const SEARCH_HOLD_MIN_MS = 15_000;
const SEARCH_HOLD_MAX_MS = 60_000;
function rollSearchHoldMs(): number {
  return SEARCH_HOLD_MIN_MS + Math.floor(Math.random() * (SEARCH_HOLD_MAX_MS - SEARCH_HOLD_MIN_MS + 1));
}
/** This run's rolled hold — read by the progress bar, re-rolled each `enter()`. */
const searchHoldMs = ref(SEARCH_HOLD_MIN_MS);

const searchSeconds = ref(0);
const searchPct = computed(() => Math.min(100, (searchSeconds.value * 1000 / searchHoldMs.value) * 100));
let tickTimer: number | null = null;
let searchToken = 0;

const wait = (ms: number) => new Promise((r) => window.setTimeout(r, ms));

/**
 * Load the standing. `load()` also runs after a bout (see onDone), and a FAILED
 * reload used to leave the PREVIOUS standing sitting on screen as if it were
 * current — so a player who had just won saw their old rating and 0W–0L under a
 * red error and reasonably concluded the win was never counted. Say plainly that
 * these are last-known numbers, and name the real problem when it is a dead
 * session (401) rather than echoing a bare "Unauthorized".
 */
async function load() {
  try {
    stats.value = await fetchArenaStats();
    error.value = '';
    stale.value = false;
  } catch (e) {
    stale.value = stats.value !== null;
    error.value = e instanceof ApiError && e.status === 401
      ? 'Your session expired, so this standing could not be refreshed — sign in again.'
      : (e as Error).message;
  } finally {
    loading.value = false;
  }
}

/**
 * Walk straight back into an unfinished bout.
 *
 * Leaving the fight screen (switching tabs, a stray click) does NOT abandon the
 * fight — the session stays live server-side for its TTL. Without this the
 * player was locked out of their own Pit: /arena/search refuses while any bout
 * is open ("Finish your current fight first.") and there was no way back in, so
 * they just waited out the timer. Now we ask on mount, and if the live bout is
 * an arena one we drop them right back on the screen.
 */
async function resumeIfFighting() {
  try {
    const live = await fetchCurrentFight();
    if (live?.active && live.kind === 'arena') {
      fightInitial.value = live;
      state.value = 'fighting';
    }
  } catch { /* nothing live — stay in the lobby */ }
}

onMounted(() => { void load(); void resumeIfFighting(); });

function stopTick() {
  if (tickTimer !== null) { window.clearInterval(tickTimer); tickTimer = null; }
}

/** Enter the Pit: kick off matchmaking. */
async function enter() {
  flowError.value = '';
  state.value = 'searching';
  searchSeconds.value = 0;
  searchHoldMs.value = rollSearchHoldMs();
  stopTick();
  tickTimer = window.setInterval(() => { searchSeconds.value += 1; }, 1000);
  const token = ++searchToken;
  const startedAt = Date.now();
  try {
    const opp = await searchArena();
    const elapsed = Date.now() - startedAt;
    if (elapsed < searchHoldMs.value) await wait(searchHoldMs.value - elapsed);
    if (token !== searchToken) return; // left the queue while this was in flight
    stopTick();
    opponent.value = opp;
    state.value = 'found';
  } catch (e) {
    if (token !== searchToken) return;
    stopTick();
    // No nerve (or any other search failure) — straight back to idle, no
    // sitting in a fake search for a match that was never going to happen.
    state.value = 'idle';
    flowError.value = (e as Error).message;
  }
}

/** Leave the queue mid-search. */
function leaveQueue() {
  searchToken += 1; // invalidate any in-flight search response
  stopTick();
  state.value = 'idle';
  void cancelArena().catch(() => { /* best-effort, cancel is idempotent */ });
}

/** FIGHT: accept the held match — spends nerve, starts the bout — then hand
 *  off to FightScreen, which resumes that SAME live session (it never
 *  starts a second one; see mobfight.service's live-session guard), so this
 *  never double-charges nerve or re-rolls the opponent. */
async function fight() {
  if (!opponent.value || busy.value) return;
  busy.value = true;
  flowError.value = '';
  try {
    fightInitial.value = await acceptArena(); // the bout we just paid for
    state.value = 'fighting';
  } catch (e) {
    flowError.value = (e as Error).message;
    opponent.value = null;
    state.value = 'idle';
  } finally {
    busy.value = false;
  }
}

/** CANCEL: drop the held match, back to idle. */
async function cancelFound() {
  if (busy.value) return;
  busy.value = true;
  try { await cancelArena(); } catch { /* ignore — cancel is idempotent */ }
  opponent.value = null;
  state.value = 'idle';
  busy.value = false;
}

async function onDone() {
  state.value = 'idle';
  opponent.value = null;
  fightInitial.value = null; // stale view must not be re-fed into the next bout
  await load();
  emit('updated');
}

// If the player navigates away mid-search or with a match held, don't leave
// a phantom hold behind server-side.
onUnmounted(() => {
  stopTick();
  if (state.value === 'searching' || state.value === 'found') void cancelArena().catch(() => {});
});
</script>

<template>
  <section>
    <!-- Fighting: hand off to the shared interactive tactical battle in arena mode -->
    <FightScreen
      v-if="state === 'fighting'"
      :arena="true"
      :hero-class="props.heroClass"
      :initial="fightInitial"
      @done="onDone"
    />

    <template v-else>
      <!-- banner -->
      <div class="pit-banner corner-frame relative rounded-lg overflow-hidden mb-5 h-40 sm:h-44">
        <img :src="BG.pit" alt="" class="w-full h-full object-cover" />
        <div class="absolute inset-0" style="background:linear-gradient(180deg,rgba(10,7,20,0.25) 30%,rgba(10,7,20,0.94))"></div>
        <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
        <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
        <div class="absolute bottom-3 left-4 sm:left-5">
          <div class="font-label uppercase tracking-[0.3em] text-[9px] text-gold-bright/80 mb-1">Ranked &middot; made man vs made man</div>
          <h1 class="font-heading text-3xl text-gold leading-none">The Pit</h1>
        </div>
      </div>
      <p class="font-body italic text-text-dim text-sm mb-6">
        Step into the Pit and settle it toe to toe. Win and you climb the ladder, lift a cut of
        their cash, and earn era standing.
      </p>

      <p v-if="error" class="mb-4 text-sm text-error">{{ error }}</p>
      <p v-if="loading" class="text-text-dim">Loading&hellip;</p>

      <template v-if="!loading && stats">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <!-- standing -->
          <div class="pit-panel corner-frame relative">
            <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
            <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
            <h2 class="font-label uppercase tracking-wider text-xs text-text-dim mb-3">
              Your standing
              <span v-if="stale" class="pit-stale" title="The last refresh failed — these are the numbers from before your bout, not your current standing.">not refreshed</span>
            </h2>
            <div class="grid grid-cols-2 gap-y-3 gap-x-2">
              <div>
                <div class="font-label uppercase tracking-wider text-[10px] text-text-dim mb-0.5">Rating</div>
                <div class="font-heading text-xl text-gold tabular-nums">{{ stats.rating }}</div>
              </div>
              <div>
                <div class="font-label uppercase tracking-wider text-[10px] text-text-dim mb-0.5">Title</div>
                <div class="font-heading text-xl text-text truncate">{{ stats.title }}</div>
              </div>
              <div>
                <div class="font-label uppercase tracking-wider text-[10px] text-text-dim mb-0.5">Record</div>
                <div class="font-heading text-xl text-text tabular-nums">{{ stats.wins }}W&ndash;{{ stats.losses }}L</div>
              </div>
              <div>
                <div class="font-label uppercase tracking-wider text-[10px] text-text-dim mb-0.5">Streak</div>
                <div class="font-heading text-xl tabular-nums" :class="stats.streak > 0 ? 'text-gold-bright' : 'text-text-dim'">
                  {{ stats.streak > 0 ? `${stats.streak} win${stats.streak === 1 ? '' : 's'}` : '—' }}
                </div>
              </div>
            </div>
          </div>

          <!-- enter / searching -->
          <div class="pit-panel corner-frame relative flex flex-col">
            <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
            <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
            <h2 class="font-label uppercase tracking-wider text-xs text-text-dim mb-3">Enter the Pit</h2>

            <template v-if="state === 'idle'">
              <p class="font-body text-text-dim text-sm mb-4 flex-1">
                Whoever answers the bell, it's toe to toe &mdash; strike, guard, or spring your class move.
              </p>
              <p v-if="flowError" class="mb-3 text-sm text-error">{{ flowError }}</p>
              <button @click="enter" class="btn-deco btn-deco--gold btn-deco--lg w-full">Enter the Pit</button>
            </template>

            <template v-else-if="state === 'searching'">
              <!-- searching — the matchmaking sequence -->
              <div class="flex-1 flex flex-col justify-center py-2">
                <div class="pit-search-bar mb-4">
                  <div class="pit-search-fill" :style="{ width: searchPct + '%' }"></div>
                </div>
                <div class="text-center mb-4">
                  <span class="font-label uppercase tracking-[0.3em] text-xs text-gold">Searching for opponent</span>
                  <span class="font-label text-text-dim text-xs"> &middot; {{ searchSeconds }}s</span>
                </div>
                <div class="flex justify-center gap-2.5 mb-1">
                  <span v-for="i in 3" :key="i" class="pit-search-dot" :style="{ animationDelay: `${i * 0.15}s` }"></span>
                </div>
              </div>
              <button @click="leaveQueue" class="btn-deco btn-deco--ghost w-full">Leave queue</button>
            </template>

            <!-- 'found': the concrete opponent card is its own full-screen overlay
                 below, on top of a translucent (not opaque) backdrop. Render
                 NOTHING here — any bright/contrasting content (this used to be the
                 "searching" bar/dots/timer, left mounted by a bare v-else that
                 covered every non-idle state) bleeds through the backdrop and reads
                 as a stray artifact behind the card. An empty panel is invisible
                 either way, so there's nothing to leak. -->
            <template v-else />
          </div>
        </div>

        <!-- recent bouts -->
        <div v-if="stats.recent.length" class="pit-panel corner-frame relative">
          <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
          <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
          <h2 class="font-label uppercase tracking-wider text-xs text-text-dim mb-3">Recent bouts</h2>
          <ul class="divide-y divide-border/40">
            <li v-for="(m, i) in stats.recent" :key="i" class="flex items-center gap-3 py-2">
              <span class="pit-bout-badge" :class="m.result === 'VICTORY' ? 'is-win' : 'is-loss'">{{ m.result === 'VICTORY' ? '▲' : '▼' }}</span>
              <span class="font-body text-sm text-text-dim flex-1 truncate min-w-0">vs {{ m.opponent }}</span>
              <span class="font-heading text-sm tabular-nums shrink-0" :class="m.ratingChange >= 0 ? 'text-gold' : 'text-error'">
                {{ m.ratingChange >= 0 ? '+' : '−' }}{{ Math.abs(m.ratingChange) }}
              </span>
            </li>
          </ul>
        </div>
      </template>

      <!-- CHALLENGE FOUND — the concrete opponent: FIGHT or CANCEL -->
      <div v-if="state === 'found' && opponent" class="fixed inset-0 z-[120] grid place-items-center p-4" style="background: rgba(4,3,10,0.88)">
        <div class="pit-found corner-frame relative w-full max-w-[400px] text-center">
          <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
          <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>

          <div class="font-label uppercase tracking-[0.34em] text-[10px] text-text-dim mb-5">Opponent found</div>

          <div class="pit-found-portrait mx-auto mb-3">
            <img :src="classArt(opponent.heroClass)" :alt="CLASS_LABELS[opponent.heroClass]" class="w-full h-full object-cover object-top" />
          </div>

          <h3 class="font-heading text-2xl text-gold-bright leading-none mb-1">{{ opponent.name }}</h3>
          <div class="font-label uppercase tracking-wider text-xs text-text-dim mb-5">{{ CLASS_LABELS[opponent.heroClass] }}</div>

          <div class="flex justify-center gap-8 mb-5">
            <div class="text-center">
              <div class="font-label uppercase tracking-wider text-[10px] text-text-dim mb-0.5">Level</div>
              <div class="font-heading text-lg text-text tabular-nums">{{ opponent.level }}</div>
            </div>
            <div class="text-center">
              <div class="font-label uppercase tracking-wider text-[10px] text-text-dim mb-0.5">Rating</div>
              <div class="font-heading text-lg text-gold tabular-nums">{{ opponent.rating }}</div>
            </div>
          </div>

          <p v-if="flowError" class="mb-4 text-sm text-error">{{ flowError }}</p>

          <div class="flex flex-col gap-2.5">
            <button :disabled="busy" @click="fight" class="btn-deco btn-deco--gold btn-deco--lg w-full">Fight</button>
            <button :disabled="busy" @click="cancelFound" class="btn-deco btn-deco--ghost w-full">Cancel</button>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<style scoped>
/* ── Banner — blood-and-sawdust vignette on top of BG.pit ────────────────── */
.pit-banner {
  box-shadow:
    inset 0 0 70px rgba(120, 10, 10, 0.28),
    inset 0 0 0 1px rgba(200, 162, 76, 0.2);
}

/* stale badge — rides the "Your standing" heading when a reload failed, so
   last-known numbers are never mistaken for the live ones */
.pit-stale {
  margin-left: 8px; padding: 2px 7px; border-radius: 999px;
  font-family: var(--font-label, sans-serif);
  font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--color-error);
  border: 1px solid color-mix(in srgb, var(--color-error) 45%, transparent);
  background: color-mix(in srgb, var(--color-error) 12%, transparent);
}

/* ── Panel — the dossier plaque used for standing / enter / ledger ───────
   Same recipe as the rest of the game's card material (hz-card / gr-pack):
   dark textured fill + a thin brass hairline signature across the top. ──── */
.pit-panel {
  position: relative;
  padding: 18px 20px;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  background:
    radial-gradient(120% 100% at 100% 0%, rgba(150, 20, 20, 0.06), transparent 60%),
    linear-gradient(165deg, rgba(20, 15, 15, 0.9), rgba(9, 6, 6, 0.94));
  box-shadow: inset 0 1px 0 rgba(255, 244, 214, 0.05);
}
.pit-panel::before {
  content: '';
  position: absolute;
  top: 0; left: 8%; right: 8%;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--color-gold-bright), transparent);
  opacity: 0.6;
}

/* ── Matchmaking — progress bar + status + pulsing dots (the "прогрузка
   матчмейкинга" the Pit was missing). Ported structure from dominion's arena
   queue block, reskinned to the noir/deco token set. ─────────────────────── */
.pit-search-bar {
  height: 4px;
  border-radius: 2px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
}
.pit-search-fill {
  height: 100%;
  border-radius: 2px;
  background: var(--color-gold);
  transition: width 0.9s linear;
}
.pit-search-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: var(--color-gold);
  animation: pit-search-pulse 1s ease-in-out infinite alternate;
}
@keyframes pit-search-pulse {
  from { opacity: 0.32; transform: scale(0.82); }
  to   { opacity: 1;    transform: scale(1.05); }
}

/* ── Challenge-found card — the concrete opponent, dominion's FIGHT/CANCEL
   popup reskinned onto the game's own panel + button language. ──────────── */
.pit-found {
  padding: 34px 30px 30px;
  border: 1px solid color-mix(in srgb, var(--color-gold) 45%, transparent);
  border-radius: 12px;
  background:
    radial-gradient(120% 100% at 100% 0%, rgba(150, 20, 20, 0.06), transparent 60%),
    linear-gradient(165deg, rgba(20, 15, 15, 0.94), rgba(9, 6, 6, 0.97));
  box-shadow: var(--shadow-lift), inset 0 1px 0 rgba(255, 244, 214, 0.05);
  animation: pit-found-rise 260ms cubic-bezier(0.22, 0.9, 0.3, 1);
}
.pit-found::before {
  content: '';
  position: absolute;
  top: 0; left: 10%; right: 10%;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--color-gold-bright), transparent);
  opacity: 0.7;
}
.pit-found-portrait {
  width: 112px; height: 140px;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid color-mix(in srgb, var(--color-gold) 45%, transparent);
  background: #14121b;
  box-shadow: 0 0 24px rgba(200, 162, 76, 0.15);
}
@keyframes pit-found-rise {
  from { opacity: 0; transform: translateY(14px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

/* ── Recent-bouts result roundel — a shrunk twin of the fight screen's
   oc-arena-badge, so the win/loss triangle reads the same everywhere. ────── */
.pit-bout-badge {
  display: grid; place-items: center; flex: none;
  width: 22px; height: 22px;
  border-radius: 999px;
  font-size: 10px; line-height: 1;
}
.pit-bout-badge.is-win {
  color: var(--color-gold-bright);
  background: radial-gradient(circle, rgba(200, 162, 76, 0.22), transparent 70%);
  border: 1px solid color-mix(in srgb, var(--color-gold) 45%, transparent);
}
.pit-bout-badge.is-loss {
  color: #d98a8a;
  background: radial-gradient(circle, rgba(192, 80, 77, 0.2), transparent 70%);
  border: 1px solid color-mix(in srgb, var(--color-error) 40%, transparent);
}

@media (prefers-reduced-motion: reduce) {
  .pit-search-dot { animation: none; opacity: 0.85; }
  .pit-search-fill { transition: none; }
  .pit-found { animation: none; }
}
</style>
